import { useOnline } from "../../hooks/useOnline";
import OfflineNotice from "../OfflineNotice/OfflineNotice";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCopy, faImage, faLanguage, faPaste, faRightLeft, faSpinner } from "@fortawesome/free-solid-svg-icons";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import SpeakButton from "../SpeakButton/SpeakButtonComponent";
import { MAX_INPUT_CHARS, translateTextWithProvider, TranslationUnavailableError, type TranslationProvider } from "../../utils/translate";
import { isPlausibleTranslation } from "../../utils/translateOutputCheck";
import { type DetectedDirection, type DetectedLanguage, detectDirectionChange } from "../../utils/detectLanguage";
import { getJapaneseRomajiPreview } from "../../utils/japaneseRomajiInput";
import { recognizeJapaneseText } from "../../utils/ocr";
import { useAuth } from "../../context/AuthContext";
import * as analytics from "../../utils/analytics";
import Seo from "../Seo/SeoComponent";
import styles from "./translatePage.module.scss";
import { useLocalizedPath } from "../../i18n/localizedPath";
import TranslationAttribution from "./TranslationAttribution";
import {
    COPY_NAMESPACE,
    type Direction,
    directionFor,
    getLanguageFlag,
    isJaSource,
    OTHER_LANGUAGES,
    otherLanguageLabelKey,
    type OtherLanguage,
    otherLanguageOf,
    SWITCH_META,
} from "../../utils/translateLanguages";

type OcrStatus = "idle" | "running";

// How the text in the box came out of the photo: plain automatic recognition,
// or with the AI second opinion (see recognizeJapaneseText).
type OcrNote = "automatic" | "ai";

// How long to wait after the learner stops typing before translating
// automatically, same as clicking the Translate button themselves.
const AUTO_TRANSLATE_DELAY_MS = 2000;

// Picking another target language is a deliberate choice, not typing, so it
// re-translates the text still in the box after a much shorter wait. Long enough
// that rapid switching (arrow keys on the select, quick clicks) only translates
// the language the learner lands on - one paid call, not one per option passed.
const TARGET_SWITCH_DELAY_MS = 400;

// The "n / 5000" counter only appears once the learner is getting close to the
// limit, so short texts never see it.
const COUNTER_VISIBLE_FROM = 4000;

// How long the Copy button shows "Copied" before going back to "Copy".
const COPIED_RESET_MS = 2000;

// Very simple N5 sentences for the tap-to-fill chips in the empty result card.
const JAPANESE_EXAMPLES = ["おはようございます", "ありがとうございます", "これはいくらですか"];

const INPUT_ID = "translate-input";
const HINT_ID = "translate-input-hint";
const COUNT_ID = "translate-input-count";
const TOO_LONG_ID = "translate-input-too-long";
const EXAMPLES_LABEL_ID = "translate-examples-label";
const ROMAJI_PREVIEW_ID = "translate-romaji-preview";

// SpeakButton's untyped props make its optional callbacks required for a typed caller.
const noop = () => {};

// The language code to use for the `lang` attribute - the translator's "zh" is
// simplified Chinese (see LANGUAGE_FLAGS in translateLanguages.ts).
function htmlLangFor(code: OtherLanguage): string {
    return code === "zh" ? "zh-Hans" : code;
}

function TranslatePage() {
    const online = useOnline();
    const lp = useLocalizedPath();
    const { t, i18n } = useTranslation("translatePage");
    const { status: authStatus, getIdToken } = useAuth();
    const location = useLocation();
    // /translate (EN UI) defaults to ja-en; /pt/translate (PT UI) defaults to
    // ja-pt - either page still lets the visitor pick the other language.
    // Based on the route path rather than i18n.language: LanguageRoute only
    // switches the active language in an effect (after this initial state
    // is computed), so i18n.language can still report the previous
    // language on this first render.
    const [direction, setDirection] = useState<Direction>(() => (location.pathname.startsWith("/pt/translate") ? "ja-pt" : "ja-en"));
    const [input, setInput] = useState("");
    const [output, setOutput] = useState("");
    // Which service produced `output` (Google, or the MyMemory fallback), so the
    // caption under the result credits the right one.
    const [outputProvider, setOutputProvider] = useState<TranslationProvider | null>(null);
    // True while the learner has edited the text since the shown translation
    // was produced: the old result stays on screen (dimmed) instead of
    // vanishing on every keystroke, until the next result replaces it.
    const [outputStale, setOutputStale] = useState(false);
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
    const [ocrStatus, setOcrStatus] = useState<OcrStatus>("idle");
    const [ocrWarning, setOcrWarning] = useState<string | null>(null);
    const [ocrNote, setOcrNote] = useState<OcrNote | null>(null);
    const [isTranslating, setIsTranslating] = useState(false);
    // OCR and clipboard problems belong with the input (left card); a failed
    // translation belongs with the result (right card), next to its retry.
    const [inputError, setInputError] = useState<string | null>(null);
    const [translateError, setTranslateError] = useState<string | null>(null);
    // The translation that was last copied (or that failed to copy). Compared
    // with the current result rather than reset by an effect, so a new
    // translation always starts without a stale "Copied" or copy error.
    const [copiedText, setCopiedText] = useState<string | null>(null);
    const [copyFailedText, setCopyFailedText] = useState<string | null>(null);
    const [detectedLanguage, setDetectedLanguage] = useState<DetectedLanguage | null>(null);
    const [romajiPreviewEdit, setRomajiPreviewEdit] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const resultSectionRef = useRef<HTMLElement>(null);
    const requestVersion = useRef(0);
    // Auto-translates 2s after the learner stops typing, same as clicking
    // the Translate button - cleared on every keystroke (so only the last
    // pause actually fires) and on any action that already handles its own
    // translation/reset (OCR, clear, swap, language change).
    const autoTranslateTimerRef = useRef<number | null>(null);
    const copiedTimerRef = useRef<number | null>(null);

    function clearAutoTranslateTimer() {
        if (autoTranslateTimerRef.current !== null) {
            window.clearTimeout(autoTranslateTimerRef.current);
            autoTranslateTimerRef.current = null;
        }
    }

    const isPt = i18n.language === "pt";
    const currentPath = isPt ? "/pt/translate" : "/translate";

    // Japanese is the source in every "ja-*" direction - OCR only ever reads
    // Japanese out of a photo, so it extends to all of them rather than
    // staying hardcoded to ja-en.
    const jaIsSource = isJaSource(direction);
    const otherLanguage: OtherLanguage = otherLanguageOf(direction);
    const sourceLang = jaIsSource ? "ja" : htmlLangFor(otherLanguage);
    const targetLang = jaIsSource ? htmlLangFor(otherLanguage) : "ja";
    const copyNs = COPY_NAMESPACE[direction];
    const copy = {
        pageTitle: t(`${copyNs}.pageTitle`),
        pageDescription: t(`${copyNs}.pageDescription`),
        seoTitle: t(`${copyNs}.seoTitle`),
        seoDescription: t(`${copyNs}.seoDescription`),
        sourceTag: t(`${copyNs}.sourceTag`),
        targetTag: t(`${copyNs}.targetTag`),
        inputLabel: t(`${copyNs}.inputLabel`),
        placeholder: t(`${copyNs}.placeholder`),
        emptyState: t(`${copyNs}.emptyState`),
        allowsOcr: jaIsSource,
    };

    const longestCopy = useMemo(() => {
        const longest = (key: "pageTitle" | "pageDescription") =>
            Object.values(COPY_NAMESPACE)
                .map((namespace) => t(`${namespace}.${key}`))
                .reduce((a, b) => (b.length > a.length ? b : a), "");
        return { pageTitle: longest("pageTitle"), pageDescription: longest("pageDescription") };
    }, [t]);

    const romajiPreview = useMemo(() => (jaIsSource ? getJapaneseRomajiPreview(input) : null), [jaIsSource, input]);
    const romajiPreviewValue = romajiPreviewEdit ?? romajiPreview?.hiragana ?? "";

    // The Lambda rejects longer text, so the learner is told up front instead
    // of getting a generic "Translation failed". Deliberately not a maxLength
    // on the textarea: that would silently truncate a long paste.
    const tooLong = input.length > MAX_INPUT_CHARS;
    const showCounter = input.length > COUNTER_VISIBLE_FROM;
    const formatCount = (value: number) => value.toLocaleString(isPt ? "pt-PT" : "en");
    const isBusy = ocrStatus === "running" || isTranslating;
    const copied = Boolean(output) && copiedText === output;
    const copyError = Boolean(output) && copyFailedText === output;

    useEffect(() => {
        return () => {
            if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
        };
    }, [imagePreviewUrl]);

    useEffect(() => clearAutoTranslateTimer, []);

    useEffect(
        () => () => {
            if (copiedTimerRef.current !== null) window.clearTimeout(copiedTimerRef.current);
        },
        []
    );

    async function handleImageChange(event: React.ChangeEvent<HTMLInputElement>) {
        const file = event.target.files?.[0];
        if (!file) return;
        await processImage(file);
    }

    // Ctrl+V / Cmd+V anywhere on the page: if the clipboard holds an image,
    // run it through OCR the same way an uploaded photo is. Plain text pastes
    // are left alone so the textarea keeps working normally. OCR only reads
    // Japanese (see docs/AWS_ARCHITECTURE.md's OCR+LLM pipeline), so this is
    // a no-op whenever Japanese isn't the source language (en-ja, pt-ja).
    const processImageRef = useRef<(image: Blob) => Promise<void>>();
    processImageRef.current = processImage;
    useEffect(() => {
        if (authStatus !== "authenticated" || !copy.allowsOcr) return;

        function handlePaste(event: ClipboardEvent) {
            const image = Array.from(event.clipboardData?.files ?? []).find((file) => file.type.startsWith("image/"));
            if (!image) return;
            event.preventDefault();
            processImageRef.current?.(image);
        }

        document.addEventListener("paste", handlePaste);
        return () => document.removeEventListener("paste", handlePaste);
    }, [authStatus, copy.allowsOcr]);

    async function handlePasteImageButton() {
        try {
            const items = await navigator.clipboard.read();
            for (const item of items) {
                const imageType = item.types.find((type) => type.startsWith("image/"));
                if (imageType) {
                    await processImage(await item.getType(imageType));
                    return;
                }
            }
            setInputError(t("errors.noClipboardImage"));
        } catch {
            setInputError(t("errors.clipboardReadFailed"));
        }
    }

    function clearImage() {
        setOcrNote(null);
        setImagePreviewUrl((current) => {
            if (current) URL.revokeObjectURL(current);
            return null;
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
    }

    // Below 1100px (see .innerGrid) the result card is stacked under the input
    // card, so after an explicit Translate or a photo it would be off-screen.
    // Not used for the automatic translation while typing: that must never move
    // the page under the learner's fingers.
    function revealResult() {
        if (!window.matchMedia?.("(max-width: 1099px)").matches) return;
        resultSectionRef.current?.scrollIntoView?.({ block: "nearest" });
    }

    async function processImage(file: Blob) {
        if (!online || navigator.onLine === false || ocrStatus === "running") return;
        clearAutoTranslateTimer();
        const version = ++requestVersion.current;
        setIsTranslating(false);

        setImagePreviewUrl((current) => {
            if (current) URL.revokeObjectURL(current);
            return URL.createObjectURL(file);
        });
        setOutput("");
        setOutputStale(false);
        setInputError(null);
        setTranslateError(null);
        setOcrWarning(null);
        setOcrNote(null);
        setOcrStatus("running");

        try {
            const { text, lowConfidence, aiAssisted } = await recognizeJapaneseText(
                file,
                authStatus === "authenticated" ? { getIdToken } : {}
            );
            if (version !== requestVersion.current) return;
            setInput(text);
            setOcrStatus("idle");
            if (!text.trim()) {
                setInputError(t("errors.noTextFound"));
            } else {
                setOcrNote(aiAssisted ? "ai" : "automatic");
                if (lowConfidence) setOcrWarning(t("errors.lowConfidence"));
                // OCR is only offered when Japanese is the source (allowsOcr),
                // so the current direction is always ja-en or ja-pt here.
                // Text over the limit stays in the box with the "too long"
                // message instead of being sent off to be rejected.
                if (text.length <= MAX_INPUT_CHARS) await runTranslation(text, direction, version, "ocr", lowConfidence, true);
            }
        } catch {
            if (version === requestVersion.current) setInputError(t("errors.ocrFailed"));
        } finally {
            if (version === requestVersion.current) setOcrStatus("idle");
        }
    }

    function applyDetectedDirection(detected: DetectedDirection) {
        setDirection(detected.direction);
        setDetectedLanguage(detected.language);
        setOcrWarning(null);
        clearImage();
    }

    async function translateExplicitText(text: string) {
        if (!online || navigator.onLine === false || isTranslating || ocrStatus === "running" || tooLong) return;
        if (!text.trim()) {
            // Translate stays pressable on an empty box (so it never looks
            // dead) - it just points the learner at the field to fill in.
            textareaRef.current?.focus();
            return;
        }
        clearAutoTranslateTimer();
        const version = ++requestVersion.current;
        // The same check the typing handler runs: covers text that got under
        // the wrong direction without being typed (e.g. kept across a swap).
        const detected = detectDirectionChange(input, direction);
        if (detected) applyDetectedDirection(detected);
        await runTranslation(
            text,
            detected?.direction ?? direction,
            version,
            imagePreviewUrl && !detected ? "ocr" : "typed",
            !detected && Boolean(ocrWarning),
            true
        );
    }

    // What the Translate button sends: the hiragana of a fully resolved romaji
    // preview (as edited by the learner), otherwise the text as it stands.
    function defaultTranslateText() {
        return romajiPreview?.fullyResolved ? romajiPreviewValue.trim() || input : input;
    }

    async function handleTranslate() {
        await translateExplicitText(defaultTranslateText());
    }

    async function handleTranslateRomajiPreview() {
        await translateExplicitText(romajiPreviewValue.trim() || input);
    }

    async function handleTranslateAsTyped() {
        await translateExplicitText(input);
    }

    async function runTranslation(
        text: string,
        target: Direction,
        version: number,
        source: "ocr" | "typed",
        lowConfidence: boolean,
        reveal = false
    ) {
        if (navigator.onLine === false) {
            // A caller can have shown "Translating..." ahead of this call (the
            // target-language switch does, during its short wait); without this the
            // spinner would stay on and Translate stay disabled.
            setIsTranslating(false);
            setTranslateError(t("errors.translationFailed"));
            return;
        }
        setIsTranslating(true);
        setInputError(null);
        setTranslateError(null);
        if (reveal) revealResult();
        try {
            const { text: result, provider } = await translateTextWithProvider(text, target);
            if (version === requestVersion.current) {
                // translate.ts only hands back a MyMemory (fallback) string that passed
                // this check. The page asks again so an unchecked one can never reach the
                // screen, nor the box tagged with the target language below.
                if (provider === "mymemory" && !isPlausibleTranslation(result, target, text)) {
                    throw new TranslationUnavailableError("fallback_rejected");
                }
                setOutput(result);
                setOutputProvider(provider);
                setOutputStale(false);
                analytics.track("translation_used", {
                    direction: target,
                    input_length: text.trim().length,
                    source,
                    ...(source === "ocr" ? { low_confidence: lowConfidence } : {}),
                });
            }
        } catch (err) {
            if (version === requestVersion.current) showTranslateError(err);
        } finally {
            if (version === requestVersion.current) setIsTranslating(false);
        }
    }

    // "No reliable translation" (Google down and the backup unusable) leaves nothing
    // on screen to trust, so the previous result is dropped along with it; any other
    // failure keeps the old, dimmed result next to the error as before.
    function showTranslateError(err: unknown) {
        if (err instanceof TranslationUnavailableError) {
            setOutput("");
            setOutputProvider(null);
            setOutputStale(false);
            setTranslateError(t("errors.translationUnreliable"));
        } else {
            setTranslateError(t("errors.translationFailed"));
        }
    }

    // `composing` is true while an IME (the Japanese keyboard) is still
    // building a word: the box then holds half-typed romaji, which must neither
    // flip the detected language nor start the auto-translate countdown. The
    // same handler runs again from onCompositionEnd once the word is committed.
    function handleInputChange(value: string, composing: boolean) {
        clearAutoTranslateTimer();
        const version = ++requestVersion.current;
        setOcrStatus("idle");
        setIsTranslating(false);
        setInput(value);
        setRomajiPreviewEdit(null);
        setInputError(null);
        setTranslateError(null);
        setOcrWarning(null);
        // Once the learner edits the text it is no longer plain photo output.
        setOcrNote(null);
        if (value.trim()) {
            setOutputStale(true);
        } else {
            // Nothing left to translate, so nothing for the old result to be stale against.
            setOutput("");
            setOutputStale(false);
        }

        if (composing) return;

        // Follows what was typed: clear evidence of another
        // language moves the pair there (a photo's OCR no longer
        // applies then); ambiguous or empty text changes nothing.
        const detected = detectDirectionChange(value, direction);
        const activeDirection = detected?.direction ?? direction;
        if (detected) {
            applyDetectedDirection(detected);
        } else if (!value.trim()) {
            setDetectedLanguage(null);
        }

        if (online && value.trim() && value.length <= MAX_INPUT_CHARS) {
            const preview = isJaSource(activeDirection) ? getJapaneseRomajiPreview(value) : null;
            const autoText = preview?.fullyResolved ? preview.hiragana : value;
            autoTranslateTimerRef.current = window.setTimeout(() => {
                autoTranslateTimerRef.current = null;
                // Reads `value`/`direction`/`imagePreviewUrl` from this
                // keystroke's closure rather than calling handleTranslate()
                // (which would read `input` state as of this render, before
                // this keystroke's setInput above has actually applied).
                void runTranslation(autoText, activeDirection, version, imagePreviewUrl && !detected ? "ocr" : "typed", false);
            }, AUTO_TRANSLATE_DELAY_MS);
        }
    }

    function handleInputKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
        if (event.key === "Enter" && (event.ctrlKey || event.metaKey) && !event.nativeEvent.isComposing) {
            event.preventDefault();
            void handleTranslate();
        }
    }

    // Tap-to-fill example: sets the text and translates it straight away
    // instead of leaving the learner to press Translate on a sentence they
    // didn't write. Only offered while the box is empty, so it can't replace
    // anything the learner typed.
    function handleExample(text: string) {
        clearAutoTranslateTimer();
        const version = ++requestVersion.current;
        setInput(text);
        setOutputStale(false);
        setInputError(null);
        setOcrWarning(null);
        setOcrNote(null);
        setDetectedLanguage(null);
        void runTranslation(text, direction, version, "typed", false, true);
    }

    // Everything an in-flight request or a previous result could still be
    // holding on to: the shared reset behind clear, swap and language change.
    // A language change that re-translates the text still in the box passes
    // `keepPhoto` (preview, "read from the photo" note and low-confidence
    // warning) and `keepRomajiEdit` (the learner's edited hiragana), because
    // both still describe that text; clear and swap reset them as ever.
    function resetSession({ keepPhoto = false, keepRomajiEdit = false }: { keepPhoto?: boolean; keepRomajiEdit?: boolean } = {}) {
        clearAutoTranslateTimer();
        ++requestVersion.current;
        setIsTranslating(false);
        setOcrStatus("idle");
        setDetectedLanguage(null);
        setOutputStale(false);
        if (!keepRomajiEdit) setRomajiPreviewEdit(null);
        setInputError(null);
        setTranslateError(null);
        if (!keepPhoto) {
            setOcrWarning(null);
            clearImage();
        }
    }

    function handleClear() {
        resetSession();
        setInput("");
        setOutput("");
    }

    // Swaps direction and carries the previous translation over as the new
    // input, mirroring how most translator apps let you keep working off a
    // result rather than starting over. A stale or still-pending result isn't
    // a translation of what's in the box, so then the learner's own text stays
    // rather than being replaced by (or wiped to) something they didn't write.
    function handleSwapDirection() {
        const carriedText = outputStale || !output ? input : output;
        resetSession();
        setDirection(SWITCH_META[direction].nextDirection);
        setInput(carriedText);
        setOutput("");
    }

    // Picking any of the supported "other" languages keeps whichever side
    // Japanese is currently on unchanged (swapping stays a separate,
    // independent control). When Japanese is the source, the input text is
    // still valid Japanese and is kept - and translated into the new language
    // after a short wait, so the learner doesn't sit under an empty result
    // beside text the page says it translates automatically. When Japanese is
    // the target, the input was typed in the previous other language, so it's
    // cleared along with the now-stale output.
    function handleSelectOtherLanguage(language: OtherLanguage) {
        if (language === otherLanguage) return;
        const nextDirection = directionFor(jaIsSource, language);

        // Decided before resetSession, which clears what this reads. Text that is
        // clearly another language (left under a Japanese source by a swap) is
        // not sent as Japanese: the Translate button corrects its direction, so
        // that is where the learner is left, as before.
        const retranslate =
            jaIsSource &&
            online &&
            ocrStatus === "idle" &&
            Boolean(input.trim()) &&
            input.length <= MAX_INPUT_CHARS &&
            !detectDirectionChange(input, nextDirection);
        const text = defaultTranslateText();
        const fromPhoto = Boolean(imagePreviewUrl);
        const lowConfidence = fromPhoto && Boolean(ocrWarning);

        resetSession({ keepPhoto: retranslate, keepRomajiEdit: retranslate });
        setDirection(nextDirection);
        if (!jaIsSource) setInput("");
        setOutput("");
        if (!retranslate) return;

        // The version resetSession just made current: the wait is cancelled, and a
        // late answer for an earlier language dropped, as soon as anything else runs.
        const version = requestVersion.current;
        setIsTranslating(true);
        autoTranslateTimerRef.current = window.setTimeout(() => {
            autoTranslateTimerRef.current = null;
            void runTranslation(text, nextDirection, version, fromPhoto ? "ocr" : "typed", lowConfidence);
        }, TARGET_SWITCH_DELAY_MS);
    }

    async function handleCopy() {
        /* istanbul ignore if -- unreachable: the Copy button is disabled while there is no translation */
        if (!output) return;
        try {
            await navigator.clipboard.writeText(output);
        } catch {
            setCopiedText(null);
            setCopyFailedText(output);
            return;
        }
        setCopyFailedText(null);
        setCopiedText(output);
        if (copiedTimerRef.current !== null) window.clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = window.setTimeout(() => {
            copiedTimerRef.current = null;
            setCopiedText(null);
        }, COPIED_RESET_MS);
    }

    const switchTargetLabel = t(SWITCH_META[direction].labelKey);

    // OTHER_LANGUAGES' own order is just insertion order, and once this
    // page displays each language's name translated into the active locale
    // (the PT page shows "Inglês", not "English"), no single fixed array
    // order can ever be alphabetical in more than one language at once. So
    // the dropdown is sorted here, at render time, by the label actually on
    // screen, using a locale-aware comparator keyed off the current i18n
    // language rather than a hardcoded English order.
    const collator = new Intl.Collator(i18n.language);
    const sortedOtherLanguages = [...OTHER_LANGUAGES]
        .map((language) => ({ ...language, label: t(otherLanguageLabelKey(language.code)) }))
        .sort((a, b) => collator.compare(a.label, b.label));

    let detectedNote = "";
    if (detectedLanguage) {
        const from = detectedLanguage === "ja" ? t("languageJapanese") : t(otherLanguageLabelKey(detectedLanguage));
        detectedNote = t("languageDetected", { from, to: copy.targetTag });
    }

    const inputDescribedBy = [HINT_ID, showCounter && COUNT_ID, tooLong && TOO_LONG_ID].filter(Boolean).join(" ");
    const showExamples = jaIsSource && !isBusy && !input.trim() && !output;
    // A backup (MyMemory) translation says "try again", so it comes with the button
    // for it; the error banner has its own, hence the !translateError.
    const showFallbackRetry = !isBusy && !translateError && Boolean(output) && outputProvider === "mymemory";

    // The result card's body: loading, result or empty, one at a time (an
    // if/else chain rather than a nested ternary).
    let resultContent: React.ReactNode;
    if (isBusy) {
        resultContent = (
            <div className={styles.emptyState}>
                <div className={styles.loadingStatus}>
                    <FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" />
                    <p>{t(isTranslating ? "translating" : "waitingForExtraction", { target: copy.targetTag })}</p>
                </div>
            </div>
        );
    } else if (output) {
        // The caption sits outside the lang-tagged box (it is in the page's
        // language, not the translation's) but inside the same live region, so
        // a result and its "automatic translation" notice are announced together.
        // A MyMemory string only gets here after the check in runTranslation, so
        // the target `lang` tag is never put on text that failed it.
        resultContent = (
            <>
                <div className={styles.resultBox} lang={targetLang} dir="auto" data-stale={outputStale}>
                    {output}
                </div>
                {outputProvider && <TranslationAttribution provider={outputProvider} />}
            </>
        );
    } else {
        resultContent = (
            <div className={styles.emptyState}>
                <FontAwesomeIcon icon={faLanguage} className={styles.emptyIcon} />
                <p>{copy.emptyState}</p>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <Seo title={copy.seoTitle} description={copy.seoDescription} path={currentPath} lang={isPt ? "pt-PT" : undefined} />
            <Header />

            <main className={styles.main}>
                <section className={styles.pageHeader}>
                    <div className={styles.pageHeaderIntro}>
                        <OfflineNotice />
                        <h1 className={`${styles.pageTitle} ${styles.reserveLongest}`} data-longest={longestCopy.pageTitle}>
                            <span>{copy.pageTitle}</span>
                        </h1>
                        <p className={`${styles.pageDescription} ${styles.reserveLongest}`} data-longest={longestCopy.pageDescription}>
                            <span>{copy.pageDescription}</span>
                        </p>
                    </div>
                </section>

                <div className={styles.directionControls}>
                    <label htmlFor="translate-other-language" className={styles.directionLabel}>
                        {t(jaIsSource ? "translateTo" : "translateFrom")}
                    </label>
                    <div className={styles.languagePicker}>
                        <select
                            id="translate-other-language"
                            className={styles.languageSelect}
                            value={otherLanguage}
                            onChange={(e) => handleSelectOtherLanguage(e.target.value as OtherLanguage)}
                        >
                            {sortedOtherLanguages.map(({ code, label }) => (
                                <option key={code} value={code}>
                                    {getLanguageFlag(code)} {label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        type="button"
                        className={styles.swapButton}
                        onClick={handleSwapDirection}
                        aria-label={t("switchToAriaLabel", { target: switchTargetLabel })}
                        title={t("switchToAriaLabel", { target: switchTargetLabel })}
                    >
                        <FontAwesomeIcon icon={faRightLeft} />
                    </button>
                </div>

                <div className={styles.innerGrid}>
                    <section className={styles.panelCard}>
                        <div className={styles.panelHeader}>
                            <div className={styles.panelTitle}>
                                <p className={styles.panelTag}>{copy.sourceTag}</p>
                                <h2 className={styles.srOnly}>{t("sourceHeading", { language: copy.sourceTag })}</h2>
                                {jaIsSource && (
                                    <SpeakButton
                                        text={input.trim()}
                                        label={t("listenToInput")}
                                        className={styles.speakButton}
                                        onPlay={noop}
                                        onPlaybackError={noop}
                                    />
                                )}
                            </div>
                            {/* Always mounted so a screen reader reliably announces the
                                note when it first gets text. */}
                            <p className={styles.detectedNote} role="status">
                                {detectedNote}
                            </p>
                        </div>

                        <label htmlFor={INPUT_ID} className={styles.srOnly}>
                            {copy.inputLabel}
                        </label>
                        <textarea
                            id={INPUT_ID}
                            ref={textareaRef}
                            value={input}
                            lang={sourceLang}
                            dir="auto"
                            onChange={(e) => handleInputChange(e.target.value, (e.nativeEvent as InputEvent).isComposing === true)}
                            onCompositionEnd={(e) => handleInputChange(e.currentTarget.value, false)}
                            onKeyDown={handleInputKeyDown}
                            aria-describedby={inputDescribedBy}
                            aria-invalid={tooLong}
                            aria-keyshortcuts="Control+Enter Meta+Enter"
                            placeholder={copy.placeholder}
                            className={styles.textarea}
                            rows={7}
                        />

                        <div className={styles.fieldHint}>
                            <p id={HINT_ID} className={styles.hintText}>
                                {t("autoTranslateHint")}
                            </p>
                            {showCounter && (
                                <p id={COUNT_ID} className={`${styles.charCount} ${tooLong ? styles.charCountOver : ""}`}>
                                    <span aria-hidden="true">
                                        {t("characterCount", { current: formatCount(input.length), max: formatCount(MAX_INPUT_CHARS) })}
                                    </span>
                                    <span className={styles.srOnly}>
                                        {t("characterCountSr", { current: formatCount(input.length), max: formatCount(MAX_INPUT_CHARS) })}
                                    </span>
                                </p>
                            )}
                        </div>

                        {tooLong && (
                            <p id={TOO_LONG_ID} className={styles.errorText} role="alert">
                                {t("tooLong", { max: formatCount(MAX_INPUT_CHARS) })}
                            </p>
                        )}

                        {romajiPreview && (
                            <div className={styles.romajiPreview}>
                                <label htmlFor={ROMAJI_PREVIEW_ID} className={styles.romajiPreviewLabel}>
                                    {t("romajiPreview.intro")}
                                </label>
                                <input
                                    id={ROMAJI_PREVIEW_ID}
                                    type="text"
                                    value={romajiPreviewValue}
                                    lang="ja"
                                    dir="auto"
                                    onChange={(e) => setRomajiPreviewEdit(e.target.value)}
                                    className={styles.romajiPreviewInput}
                                />
                                <div className={styles.romajiPreviewActions}>
                                    <button
                                        type="button"
                                        className={`${styles.actionButton} ${styles.secondaryButton}`}
                                        onClick={handleTranslateRomajiPreview}
                                        disabled={!online || isTranslating || ocrStatus === "running" || tooLong}
                                    >
                                        <Trans
                                            t={t}
                                            i18nKey="romajiPreview.translateConverted"
                                            values={{ text: romajiPreviewValue }}
                                            components={{ jp: <span lang="ja" className={styles.romajiPreviewConvertedLabel} /> }}
                                        />
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.actionButton} ${styles.secondaryButton}`}
                                        onClick={handleTranslateAsTyped}
                                        disabled={!online || isTranslating || ocrStatus === "running" || tooLong}
                                    >
                                        {t("romajiPreview.translateTyped")}
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className={styles.actionRow}>
                            <button
                                type="button"
                                className={`${styles.actionButton} ${styles.primaryButton}`}
                                onClick={handleTranslate}
                                disabled={!online || isTranslating || ocrStatus === "running" || tooLong}
                            >
                                {isTranslating ? t("translating") : t("translate")}
                            </button>
                            <button
                                type="button"
                                className={`${styles.actionButton} ${styles.clearButton}`}
                                onClick={handleClear}
                                disabled={!input && !output && !imagePreviewUrl}
                            >
                                {t("clear")}
                            </button>
                        </div>

                        {!online && <p className={styles.helperText}>{t("offline.connectionRequired", { ns: "common" })}</p>}

                        {copy.allowsOcr &&
                            (authStatus === "authenticated" ? (
                                <>
                                    <div className={styles.imageActions}>
                                        <label className={styles.uploadButton}>
                                            <FontAwesomeIcon icon={faImage} />
                                            {ocrStatus === "running" ? t("readingTextFromImage") : t("uploadPhotoInstead")}
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className={styles.srOnly}
                                                disabled={!online || ocrStatus === "running"}
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            className={styles.uploadButton}
                                            onClick={handlePasteImageButton}
                                            disabled={!online || ocrStatus === "running"}
                                        >
                                            <FontAwesomeIcon icon={faPaste} />
                                            {t("pasteImage")}
                                        </button>
                                    </div>
                                    <p className={styles.pasteHint}>{t("pasteHint")}</p>
                                </>
                            ) : (
                                <Link to={lp("/login")} state={{ from: location }} className={styles.uploadButton}>
                                    <FontAwesomeIcon icon={faImage} />
                                    {t("signInToUploadPhoto")}
                                </Link>
                            ))}

                        {jaIsSource && (
                            <p className={styles.helperText}>
                                <Trans
                                    t={t}
                                    i18nKey="noJapaneseKeyboard"
                                    components={{
                                        keyboard: <Link to={lp("/keyboard")} className={styles.inlineLink} />,
                                        romaji: <Link to={lp("/romaji-converter")} className={styles.inlineLink} />,
                                    }}
                                />
                            </p>
                        )}

                        {imagePreviewUrl && (
                            <img src={imagePreviewUrl} alt={t("imageAlt")} className={styles.imagePreview} />
                        )}

                        {imagePreviewUrl && ocrNote && <p className={styles.ocrNote}>{t(`ocrNote.${ocrNote}`)}</p>}

                        {inputError && (
                            <p className={styles.errorText} role="alert">
                                {inputError}
                            </p>
                        )}

                        {ocrWarning && (
                            <p className={styles.warningText} role="status">
                                {ocrWarning}
                            </p>
                        )}
                    </section>

                    <section className={styles.panelCard} ref={resultSectionRef}>
                        <div className={styles.panelHeader}>
                            <div className={styles.panelTitle}>
                                <p className={styles.panelTag}>{copy.targetTag}</p>
                                <h2 className={styles.srOnly}>{t("targetHeading", { language: copy.targetTag })}</h2>
                            </div>
                            <div className={styles.resultActions}>
                                {!jaIsSource && output && (
                                    <SpeakButton
                                        text={output}
                                        label={t("listenToTranslation")}
                                        className={styles.speakButton}
                                        onPlay={noop}
                                        onPlaybackError={noop}
                                    />
                                )}
                                <button
                                    type="button"
                                    className={`${styles.copyButton} ${styles.secondaryButton}`}
                                    onClick={handleCopy}
                                    disabled={!output}
                                    aria-label={copied ? t("copied") : t("copyTranslation")}
                                >
                                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                                    {copied ? t("copied") : t("copy")}
                                </button>
                                {/* Persistent so the "Copied" confirmation is announced. */}
                                <span className={styles.srOnly} aria-live="polite">
                                    {copied ? t("copied") : ""}
                                </span>
                            </div>
                        </div>

                        {copyError && (
                            <p className={styles.errorText} role="alert">
                                {t("copyError")}
                            </p>
                        )}

                        {translateError && (
                            <div className={styles.translateError} role="alert">
                                <p>{translateError}</p>
                                <button type="button" className={`${styles.retryButton} ${styles.secondaryButton}`} onClick={handleTranslate}>
                                    {t("retry")}
                                </button>
                            </div>
                        )}

                        <div className={styles.resultBody}>
                            {/* One always-mounted live region around loading / result /
                                empty, so each change is announced once (a region that
                                mounts together with its content is often skipped). */}
                            <div className={styles.liveRegion} aria-live="polite" aria-atomic="true">
                                {resultContent}
                            </div>

                            {/* Outside the live region: a button that appears there is announced along with the result. */}
                            {showFallbackRetry && (
                                <button
                                    type="button"
                                    className={`${styles.retryButton} ${styles.secondaryButton} ${styles.fallbackRetry}`}
                                    onClick={handleTranslate}
                                    disabled={!online}
                                >
                                    {t("retry")}
                                </button>
                            )}

                            {showExamples && (
                                <div className={styles.examples}>
                                    <p id={EXAMPLES_LABEL_ID} className={styles.examplesLabel}>
                                        {t("examplesLabel")}
                                    </p>
                                    <div className={styles.examplesRow} role="group" aria-labelledby={EXAMPLES_LABEL_ID}>
                                        {JAPANESE_EXAMPLES.map((example) => (
                                            <button
                                                key={example}
                                                type="button"
                                                lang="ja"
                                                className={styles.exampleButton}
                                                onClick={() => handleExample(example)}
                                                disabled={!online}
                                            >
                                                {example}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </main>

            <Footer />
        </div>
    );
}

export default TranslatePage;
